import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import { formatDay, relativeTime } from '../lib/time'
import { childCount, depthOf, visibleWithCollapse } from '../lib/tree'
import type { EmailTask } from '../types'

type ItemProps = {
  task: EmailTask
  byId: Map<string, EmailTask>
  listRows: EmailTask[]
  expanded: boolean
  onToggleExpand: (id: string) => void
  listSearch: string
}

function shortEmail(email: string | null): string {
  if (!email) return ''
  const at = email.indexOf('@')
  if (at <= 0) return email
  return email.slice(0, at)
}

export function TaskListItem({
  task,
  byId,
  listRows,
  expanded,
  onToggleExpand,
  listSearch,
}: ItemProps) {
  const depth = depthOf(task, byId)
  const kidsInList = childCount(task.id, listRows)
  const due = formatDay(task.due_at)
  const seen = relativeTime(task.last_seen_at)
  const assigner = task.assigned_by_email || task.origin_email
  const preview = (task.source_quote || task.detail || '').trim()
  const isSubtask = Boolean(task.parent_id && byId.has(task.parent_id))

  return (
    <li
      className={`todo status-${task.status}${isSubtask ? ' is-child' : ''}${
        kidsInList ? ' has-children' : ''
      }${expanded ? ' is-expanded' : ''}`}
      style={{ '--depth': String(depth) } as CSSProperties}
      data-subtask={isSubtask ? 'true' : undefined}
    >
      {kidsInList > 0 ? (
        <button
          type="button"
          className="todo-disclose"
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse subtasks' : 'Expand subtasks'}
          onClick={() => onToggleExpand(task.id)}
        >
          <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
            <path
              d={expanded ? 'M4 6.5 8 10.5 12 6.5' : 'M6 4 10 8 6 12'}
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      ) : isSubtask ? (
        <span className="todo-disclose child-mark" aria-hidden="true">
          ·
        </span>
      ) : (
        <span className="todo-disclose spacer" aria-hidden="true" />
      )}
      <Link
        to={`/tasks/${task.id}`}
        state={{ listSearch }}
        className="todo-body"
      >
        <span className="todo-title">{task.title}</span>
        {preview ? (
          <span className="todo-preview">
            {preview.slice(0, 140)}
            {preview.length > 140 ? '…' : ''}
          </span>
        ) : null}
        <span className="todo-meta">
          <span className={`pill status-pill status-${task.status}`}>
            {task.status === 'wrong_redo' ? 'Wrong / Redo' : task.status}
          </span>
          {task.priority ? (
            <span className={`pill prio-${task.priority}`}>{task.priority}</span>
          ) : null}
          {due ? <span className="pill">due {due}</span> : null}
          {task.owner_email ? (
            <span className="pill muted" title={task.owner_email}>
              owner {shortEmail(task.owner_email)}
            </span>
          ) : null}
          {assigner ? (
            <span className="pill muted" title={assigner}>
              from {shortEmail(assigner)}
            </span>
          ) : null}
          {kidsInList > 0 ? (
            <span className="pill muted">{kidsInList} sub</span>
          ) : null}
          {task.mention_count > 1 ? (
            <span className="pill muted">×{task.mention_count}</span>
          ) : null}
          <span className="pill muted" title={task.last_seen_at ?? undefined}>
            seen {seen}
          </span>
        </span>
      </Link>
    </li>
  )
}

type ListProps = {
  rows: EmailTask[]
  byId: Map<string, EmailTask>
  listKey: string
  listSearch: string
}

/** Renders TaskListItems; subtasks indented and collapsed by default. */
export function TaskList({ rows, byId, listKey, listSearch }: ListProps) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())

  useEffect(() => {
    setExpanded(new Set())
  }, [listKey])

  const visible = useMemo(
    () => visibleWithCollapse(rows, byId, expanded),
    [rows, byId, expanded],
  )

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  if (!rows.length) {
    return <p className="empty">No todos match.</p>
  }

  return (
    <ul className="todo-list" key={listKey}>
      {visible.map((task) => (
        <TaskListItem
          key={task.id}
          task={task}
          byId={byId}
          listRows={rows}
          expanded={expanded.has(task.id)}
          onToggleExpand={toggle}
          listSearch={listSearch}
        />
      ))}
    </ul>
  )
}
